import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Users, Send, RefreshCw, Trash2, Rocket } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const SERVICES = ['rides', 'bus', 'logistics'];

const AdminCityWaitlist = () => {
  const [cityFilter, setCityFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(null); // {city, service}
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyChannel, setNotifyChannel] = useState('whatsapp');
  const [notifying, setNotifying] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityFilter) params.set('city', cityFilter);
      if (serviceFilter) params.set('service', serviceFilter);
      const { data } = await api.get(`/admin/city-waitlist?${params.toString()}`);
      setItems(data.items || []);
      setSummary(data.summary || []);
    } catch {
      toast({ title: 'Load failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const deleteRow = async (id) => {
    if (!window.confirm('Remove this entry from the waitlist?')) return;
    try {
      await api.delete(`/admin/city-waitlist/${id}`);
      fetchAll();
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const openNotify = (city, service) => {
    setNotifyOpen({ city, service });
    setNotifyMsg(`🎉 Great news — ${service.toUpperCase()} is now live in ${city.charAt(0).toUpperCase() + city.slice(1)} on Tekstar! Open the app to book your first trip.`);
    setNotifyChannel('whatsapp');
  };
  const sendNotify = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post('/admin/city-waitlist/notify', {
        city: notifyOpen.city,
        service: notifyOpen.service,
        message: notifyMsg,
        channel: notifyChannel,
        only_pending: true,
      });
      toast({
        title: 'Launch blast sent',
        description: `Sent ${data.sent}/${data.eligible} (${data.failed} failed)`,
      });
      setNotifyOpen(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setNotifying(false); }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-waitlist-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="h-6 w-6 text-pink-500" /> City Launch Waitlist
            </h1>
            <p className="text-sm text-gray-500">
              Visitors who tried to book in a city where the service is disabled. One-tap launch blast when you flip it live.
            </p>
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="waitlist-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Demand by city × service</h3>
          {summary.length === 0 ? (
            <p className="text-sm text-gray-500">No waitlist demand yet — share the public app in cities you haven&apos;t launched.</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-3">
              {summary.map((s, idx) => (
                <Card key={`${s.city}-${s.service}`} className="p-4" data-testid={`waitlist-summary-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">{s.service}</p>
                      <p className="text-lg font-bold capitalize">{s.city}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-pink-100 text-pink-700 font-semibold">
                      {s.count}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Pending <strong>{s.pending}</strong> · Notified <strong>{s.notified}</strong>
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 w-full bg-pink-600 hover:bg-pink-700 text-white"
                    onClick={() => openNotify(s.city, s.service)}
                    disabled={s.pending === 0}
                    data-testid={`waitlist-notify-${idx}`}
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    {s.pending === 0 ? 'All notified' : `Notify ${s.pending}`}
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Filters + list */}
        <Card className="p-5 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[140px]">
              <Label>City slug</Label>
              <Input
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value.toLowerCase())}
                placeholder="kasama"
                data-testid="waitlist-city-filter"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label>Service</Label>
              <select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="waitlist-service-filter"
              >
                <option value="">All</option>
                {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Button onClick={fetchAll} data-testid="waitlist-apply-filters-btn">Apply</Button>
          </div>

          <div className="divide-y" data-testid="waitlist-list">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No entries match the filter.</p>
            ) : items.map(row => (
              <div key={row.id} className="py-3 flex items-center justify-between gap-3" data-testid={`waitlist-row-${row.id}`}>
                <div>
                  <p className="font-medium text-sm">
                    {row.name || row.phone} · <span className="capitalize text-gray-500">{row.service}</span>
                    {' · '}<span className="capitalize">{row.city}</span>
                  </p>
                  <p className="text-xs text-gray-500 font-mono">{row.phone}{row.email ? ` · ${row.email}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {row.notifiedAt ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Notified</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Pending</span>
                  )}
                  <button
                    onClick={() => deleteRow(row.id)}
                    className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Notify modal */}
        {notifyOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setNotifyOpen(null)}>
            <Card className="w-full max-w-lg p-5 space-y-4 bg-white" onClick={(e) => e.stopPropagation()} data-testid="waitlist-notify-modal">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Send className="h-5 w-5 text-pink-500" />
                Launch blast — {notifyOpen.service} · <span className="capitalize">{notifyOpen.city}</span>
              </h3>
              <div>
                <Label>Channel</Label>
                <div className="flex gap-2 mt-1">
                  {['whatsapp', 'sms'].map(ch => (
                    <button
                      key={ch}
                      onClick={() => setNotifyChannel(ch)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${notifyChannel === ch ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-600 border-gray-300'}`}
                      data-testid={`waitlist-notify-channel-${ch}`}
                    >{ch.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Message</Label>
                <textarea
                  value={notifyMsg}
                  onChange={(e) => setNotifyMsg(e.target.value)}
                  maxLength={240}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-28"
                  data-testid="waitlist-notify-msg"
                />
                <p className="text-xs text-gray-500 mt-1">{notifyMsg.length}/240</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNotifyOpen(null)}>Cancel</Button>
                <Button
                  onClick={sendNotify}
                  disabled={notifying || notifyMsg.length < 10}
                  className="bg-pink-600 hover:bg-pink-700 text-white"
                  data-testid="waitlist-notify-send-btn"
                >
                  {notifying ? 'Sending…' : 'Send blast'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCityWaitlist;
